import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, Renderer2, ComponentFactoryResolver, ViewContainerRef, NgZone, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router, ActivatedRoute } from '@angular/router';
import { LicenseManager, ColDef, GridApi, ColumnApi, GridOptions, IsRowSelectable, IServerSideDatasource, StatusPanelDef, ColGroupDef, EditableCallbackParams, CellClassParams } from 'ag-grid-enterprise';
import { TableService } from 'src/app/services/table.service';
import { ThemeService } from 'src/app/services/theme.service';
import { TableCommunicationService } from './services/table-communication.service';
import { AgGridUserSettings } from './services/ag-grid-user-settings.service';
import { AgGridFiltersService } from './services/ag-grid-filters.service';
import { AgGridToolsService } from './services/ag-grid-tools.service';
import { AgGridEditService } from './services/ag-grid-edit.service';
import { Subscription, Subject, from } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { ColumnPresetsComponent } from './sidebar-components/column-presets/column-presets.component';
import { MainStatusPanelComponent } from './ag-grid-status-panels/main-panel/main-status-panel.component';
import { CmsFiltersComponent } from './sidebar-components/cms-filters/cms-filters.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { UserContextService } from '../services/user-context.service';
import { UtilitiesService } from '../services/utilities.service';
import { GettersService } from 'src/app/common/services/getters.service';
import * as LZString from 'lz-string';
import { Title } from '@angular/platform-browser';
import { GridViewComponent } from '../components/grid-view/grid-view.component';
import { environment } from 'src/environments/environment';
import { DialogService } from './services/ag-grid-dialog.service';
import { GridViewSettingsComponent } from './sidebar-components/grid-view-settings/grid-view-settings.component';
import { DupDialogComponent } from '../components/dup-dialog/dup-dialog.component';
import { AgTableActionsComponent } from './ag-grid-actions/ag-grid-actions.component';
import { TABLE_ACTIONS } from './constants';
import { ItemSetsToolPanelComponent } from './sidebar-components/item-sets-tool-panel/item-sets-tool-panel.component';
import { BooleanEditorComponent } from './cell-editors/boolean/boolean.component';
import { CalendarEditorComponent } from './cell-editors/calendar/calendar.component';
import { AssistantService } from './services/assistant/assistant.service';
import { AiAssistantService } from 'src/app/services/ai-assistant/ai-assistant.service';
import isEqual from 'lodash/isEqual';
import { HttpClient } from '@angular/common/http';
import { FloraColDef, MOBILE_BREAKPOINT_PX } from './ag-grid-mobile.columns';
import { AiTableSearchChange, AiTableSearchMode, loadAiTableSearchMode } from './components/ai-table-search/ai-table-search.component';
import { buildAiTagQuery, buildFallbackQuery } from './components/ai-table-search/ai-tag-query';

@Component({
  selector: 'app-ag-grid-table',
  templateUrl: './ag-grid-table.component.html',
  styleUrls: [
    './ag-grid-table.component.css',
    './ag-grid-scroll-container.sass',
    './ag-grid-table-toolbar.sass',
  ],
  providers: [ConfirmationService, MessageService],
})
export class AgGridTableComponent implements OnInit, OnDestroy {
  // AGGRID License manager:
  licence: any = LicenseManager.setLicenseKey(environment.agGridLicense)

  @Input() entity: string;
  @Input() tableIdentifier: string | null = null;
  tableHeight: string = 'auto';
  @Input() customHeight: string | null = null;
  @Input() viewRoute: string;
  @Input() columnDefs: ColDef[] = [];
  isMobile = false;
  isCompactToolbar = false;
  isToolbarSidebarOpen = false;
  private baseColumnDefs: FloraColDef[] = [];
  private breakpointSubscription?: Subscription;
  private compactToolbarSubscription?: Subscription;
  public defaultColDef: ColDef = {
    filterParams: {
      suppressAndOrCondition: true
    },
    cellClass: 'center-cell-content',
    wrapText: true,
    autoHeight: true
  }
  @Input() bulkUpdateColDefs: any[] = [];
  @Input() columnSelection: string;
  @Input() rowSelection: string = 'multiple';
  @Input() autopopulateSelect: string = '';
  @Input() buildParams: any = {};
  @Input() tableParams: any = {};
  @Input() isStoreEntity: boolean = false;
  @Input() isViewEmbedded: boolean = false;
  @Input() nestedPopulate: boolean = false;
  @Input() isTableInput: boolean = false;
  @Input() isDynamicListTable: boolean = false;
  @Input() tableInputParams: any = null;
  @Input() itemsRef: any[];
  @Input() isDynamicEntity: boolean = false;
  @Input() context: any;
  @Input() isItemSetsPanelVisible: boolean = false;
  @Input() assistantEnabled: boolean = false;
  @Input() isAiAssistantPanelVisible: boolean = false;
  @Input() hideLocalizationButton: boolean = false;

  populateFields: any;
  rowData: any[];
  loadingGrid: boolean = false;
  @Input() parentViewName: any;

  dialogVisible: boolean = false;
  dialogData: { header: string; content: string; footerLabel: string, ids?: any } | null = null;
  

  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() aiAssistantRequested = new EventEmitter<void>();
  @Output() openPopupFormEvent = new EventEmitter<{action: 'new' | 'edit', recordId?: number}>();
  displayDialog: boolean = false;
  selectedRows: any[];

  TABLE_ACTIONS = TABLE_ACTIONS
  filterCounter: any;
  sortCounter: any;
  isStartSortAutoApplied: boolean = false;

  // USER
  public userParams: any = {
    user: {}
  };

  // EDIT MODE VARIABLES
  @Input() fullEditModeEnabled: boolean = true;
  public isFullEditMode: boolean = false;
  public activeRowEdit: boolean = false;
  public isLoading: boolean = true;

  public rows: any[] = [];
  public gridOptions: GridOptions;
  public changedRows: Map<number, any> = new Map();  // Keeps track of changed rows by record ID
  public originalData: Map<string, any> = new Map(); // Keeps track of original values for changed rows by "id-field" key


  public api: GridApi;
  public columnApi: ColumnApi;

  private saveColumnStateTimeout: any;
  private userData: any = {};

  public gridTheme: string; // Default to the light theme
  public themeSubscription: Subscription;
  private editRowSubscription: Subscription;
  private cancelEditRowSubscription: Subscription;
  private submitSaveRowSubscription: Subscription;
  private fullEditModeSubscription: Subscription;
  private pageDataSubscription: Subscription;

  public timestamp = new Date().getTime();
  public dialogImageUrl: string | null = null;
  public dialogImageTitle: string | null = null;

  public areRowsSelected: boolean = false;
  public rowsSelected: any[] = [];
  public loadedRowCount: number = 0;
  public totalRowCount: number = 0;
  public tableTotalRecords: number = 0;
  private previousFilterModel: any = null;
  public isSelectAll: boolean = false;
  public mongoQuery: any = {};
  public mongoSort: any = {};
  // column selection
  public lastQueryMatchingIds: Set<number> = new Set<number>();
  public selectedRowsSet: Set<number> = new Set<number>();

  public globalSearchTerm: string = '';
  private searchSubject = new Subject<string>();
  public globalSearchFilter: string = '';
  /** Which query shape the AI-enabled search box is currently building. Starts from the viewer's last pick (Name by default). */
  public aiSearchMode: AiTableSearchMode = loadAiTableSearchMode();

  // custom query buttons
  public showArchived: boolean = false; // false means don't show, true means show
  public hideActions: boolean = false

  // MISC Asset Dialog
  public displayAssetDialog: boolean = false;
  public displayStringDialog: boolean = false;
  public displayImportCsvDialog: boolean = false;
  public importCsvTriggerTranslation: boolean = true;
  public importCsvSkipExisting: boolean = true;
  public importCsvErrors: Array<{ row: number; message: string }> = [];
  public importCsvUploading: boolean = false;
  public importCsvSelectedFileName: string | null = null;
  public importCsvSelectedFile: File | null = null;
  public entityTypeSettings: Array<any> = [];
  public entityTypeOptions: Array<any> = [];
  public imagerySettings: Array<any> = [];
  public entityTypesLoaded: boolean = false;
  public imagerySettingsLoaded: boolean = false;
  public miscAssetEntity: any = {
    id: null,
    name: '',
    entityType: null,
    assetType: null,
    path: '',
  };

  // grid view
  private isGridViewVisible: boolean = true;
  private componentRef: any; // Holds reference to the dummy element
  private dialogSubscription: Subscription;
  private currentPage: number = 0;

  showDynamicForm: boolean = false;
  dynamicFormFields: any[] = [];

  @Input() excludeFields: string[] = ['createdAt', 'createdBy', 'updatedAt', 'updatedBy']; // Default excluded fields
  @Input() includeFields: string[] = []; // Optional: specify only these fields to include

  get urlNewForm(): string {
    // Allow custom URL to be set through tableParams
    if (this.tableParams && this.tableParams.newFormUrl) {
      return this.tableParams.newFormUrl;
    }
    
    // grab url from router
    const url = this.router.url;
    if (url.includes('/dynamic-entities')) { 
      return `/dynamic-entities/add`
    } else if (this.isDynamicEntity) {
      return `/d/${this.entity}/add`
    } else {
      return `/${this.entity}/add`
    }
  }

  // To disable table's actions
  public disabledTableActions: Array<TABLE_ACTIONS> = [];

  // EDIT
  public editingRows: Set<number> = new Set();

  /**
   * The user's write abilities on `tableParams.permissionController` (a CASL subject such as
   * 'ItemController'), which gate New, inline/full edit, duplicate, delete and bulk actions. A table
   * that names no controller keeps every control, as before. The API enforces the same abilities;
   * this only stops the UI from offering what it would reject (CMS-6343).
   */
  public canCreate = true;
  public canUpdate = true;
  public canDelete = true;
  public columnTypes: {
    [key: string]: ColDef;
  } = {
      editableColumn: {
        editable: (params: EditableCallbackParams<any>) => {
          return this.isCellEditable(params);
        },
        cellClass: (params: any) => {
          if (this.isFullEditMode || this.editingRows.has(params.node.rowIndex)) {
            return 'center-cell-content editing-cell';
          } else {
            return 'center-cell-content';
          }
        }
      },
    };

  // tool panels management
  isColumnsPanelVisible = false;
  isColumnPresetsPanelVisible = false;
  isFiltersPanelVisible = false;
  isSavedFiltersPanelVisible = false;
  isGridViewSettingsPanelVisible = false;

  columnState: any;

  currentSortModel: any = {};
  currentFilterModelVars: any = {};
  
  // Async options filter support
  private shouldDeferFilterApplication: boolean = false;
  private pendingFilterApplication: boolean = false;

  @Input() isPrizeTable: boolean = false;
  @Input() prizeCounts: { [key: string]: number } = {};

  @ViewChild(DupDialogComponent) dupDialogComponent!: DupDialogComponent;
  @ViewChild('actionsDialog') actionsDialog!: AgTableActionsComponent;
  @ViewChild('agGrid', { read: ElementRef }) agGridHost?: ElementRef<HTMLElement>;

  private static nextTableInstanceId = 0;
  public readonly tableInstanceId = `ag-grid-table-${++AgGridTableComponent.nextTableInstanceId}`;
  private duplicateRowSubscription: Subscription;
  private deleteRowSubscription: Subscription;

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressOrigin: { x: number; y: number; rowIndex: number; colId: string } | null = null;
  private longPressUnlisteners: Array<() => void> = [];
  private readonly LONG_PRESS_MS = 480;
  private readonly LONG_PRESS_MOVE_TOLERANCE_PX = 12;

  showActionsPanel(): void {
    this.actionsDialog?.show();
  }


  constructor(
    private tableService: TableService,
    private tableCommunicationService: TableCommunicationService,
    private themeService: ThemeService,
    private agGridUserSettings: AgGridUserSettings,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private agGridFiltersService: AgGridFiltersService,
    private agGridToolsService: AgGridToolsService,
    private agGridEditService: AgGridEditService,
    private confirmationService: ConfirmationService,
    private userContextService: UserContextService,
    private utilitiesService: UtilitiesService,
    private gettersService: GettersService,
    private titleService: Title,
    private renderer: Renderer2,
    private componentFactoryResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private messageService: MessageService,
    private assistantService: AssistantService,
    private aiAssistantService: AiAssistantService,
    private http: HttpClient,
    private breakpointObserver: BreakpointObserver,
  ) {
    //
    this.tableCommunicationService.event$.subscribe(event => {
      if (event?.type === 'showImage') {
        if(event.tableIdentifier !== null && this.tableIdentifier !== event.tableIdentifier) {
          return;
        }

        this.dialogImageUrl = event.imageUrl;
        this.dialogImageTitle = event.imageTitle
      }
      // Handle other event types as needed in the future
    });
    this.dialogSubscription = this.dialogService.dialogState.subscribe(state => {
      this.dialogImageUrl = state.imageUrl;
      this.dialogImageTitle = state.title;
      // Logic to show/hide dialog based on state.visible
    });
    this.pageDataSubscription = this.tableCommunicationService.requestCurrentPageData$.subscribe(() => {
      this.updateCurrentPageData();
    });
    this.duplicateRowSubscription = this.tableCommunicationService.duplicateRow$.subscribe(event => {
      if (!this.isEventForThisTable(event)) {
        return;
      }
      this.showDuplicateDialog(event);
    });

    this.deleteRowSubscription = this.tableCommunicationService.deleteRow$.subscribe(event => {
      if (!this.isEventForThisTable(event)) {
        return;
      }
      this.handleRowDeletion(event);
    });
  }

  async ngOnInit(): Promise<void> {
    this.tableHeight = this.customHeight && this.customHeight !== 'null' ? this.customHeight : '70vh';
    this.isLoading = true;
    this.applyWritePermissions();
    if (this.isViewEmbedded) {
      this.isColumnsPanelVisible = true;
    }
    // Ensure isDynamicEntity is set in tableParams if it's provided as an input
    if (this.isDynamicEntity && !this.tableParams.isDynamicEntity) 
    {
      this.tableParams = { ...this.tableParams, isDynamicEntity: true };
    }
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
    this.baseColumnDefs = this.agGridToolsService.preprocessColumnDefs([
      ...this.columnDefs,
    ]) as FloraColDef[];
    this.updateColumnDefsForViewport();
    this.breakpointSubscription = this.breakpointObserver
      .observe([`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`])
      .subscribe((state) => {
        const wasMobile = this.isMobile;
        this.isMobile = state.matches;
        if (wasMobile !== this.isMobile) {
          if (this.isMobile && this.isFullEditMode) {
            this.isFullEditMode = false;
            this.editingRows.clear();
            this.activeRowEdit = false;
            this.api?.redrawRows();
          }
          this.updateColumnDefsForViewport();
        }
      });

    this.isCompactToolbar = window.innerWidth < 1024;
    this.compactToolbarSubscription = this.breakpointObserver
      .observe(['(max-width: 1023px)'])
      .subscribe((state) => {
        this.isCompactToolbar = state.matches;
        if (!state.matches) {
          this.isToolbarSidebarOpen = false;
        }
        this.cdr.markForCheck();
      });

    this.selectedRowsSet.clear();

    if (!this.isViewEmbedded && !this.isTableInput) {
      let title = this.parse(this.entity) + ' Table';
      this.titleService.setTitle(title);
    }

    this.getUserData();

    this.gridOptions = {
      context: this.context || {},
      rowSelection: 'multiple',
      rowModelType: 'serverSide',
      // debug: true,
      serverSideDatasource: this.createServerSideDatasource(),
      // pagination
      pagination: true,
      paginationPageSize: 50,
      cacheBlockSize: this.tableParams.isMultiple ? 5000 : 50,
      maxBlocksInCache: this.tableParams.isMultiple ? 1 : 2,
      suppressPaginationPanel: true,
      blockLoadDebounceMillis: 100,
      // sorting
      alwaysMultiSort: true,
      suppressContextMenu: true,
      ensureDomOrder: true,
      enableCellTextSelection: true,
      sideBar: {
        hiddenByDefault: !this.isColumnsPanelVisible,
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true
            }
          },
          {
            id: 'columnPresets',
            labelDefault: 'Column Presets',
            labelKey: 'columnPresets',
            iconKey: 'columns',
            toolPanel: ColumnPresetsComponent,
            toolPanelParams: {
              entity: this.entity,
            }
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
          },
          {
            id: 'cmsFilters',
            labelDefault: 'Saved Filters',
            labelKey: 'cmsFilters',
            iconKey: 'filter',
            toolPanel: CmsFiltersComponent,
            toolPanelParams: {
              entity: this.entity,
            }
          },
          {
            id: 'gridViewSettings',
            labelDefault: 'Grid View Settings',
            labelKey: 'gridViewSettings',
            iconKey: 'table',
            toolPanel: GridViewSettingsComponent,
            width: 300,
            toolPanelParams: {
              entity: this.entity,
              fields: this.columnDefs.map((obj: any) => ({ headerName: obj.headerName, key: obj.field }))
            }
          },
          ...(this.entity === 'items' && !this.isViewEmbedded ? [{
            id: 'itemSets',
            labelDefault: 'Item Sets',
            labelKey: 'itemSets',
            iconKey: 'menu',
            toolPanel: ItemSetsToolPanelComponent,
            width: 680,
            minWidth: 680,
            toolPanelParams: {
              parentEntities: ['item-sets']
            }
          }] : [])
        ]
      },
      columnTypes: this.columnTypes,
      rowGroupPanelShow: "never",
      suppressRowClickSelection: true,
      singleClickEdit: false,
      stopEditingWhenCellsLoseFocus: false,
      // editType: 'fullRow',
      statusBar: {
        statusPanels: [
          {
            statusPanel: MainStatusPanelComponent, align: 'center',
            statusPanelParams: {
              entity: this.entity,
            }
          }
        ],
      },
      onColumnVisible: this.onColumnVisible.bind(this),
      onColumnMoved: this.onColumnMoved.bind(this),
      onColumnResized: this.onColumnResized.bind(this),
      onFilterChanged: this.updateURLWithFilter.bind(this),
      onSortChanged: this.onSortChanged.bind(this),
      onCellValueChanged: this.onCellValueChanged.bind(this),
      getRowId: (params) => {
        return params.data.id;
      },
      // domLayout: 'autoHeight', // this breaks tables embedded in views
    };
    //
    this.userData = await this.authService.getSocialUser();

    this.themeSubscription = this.themeService.isDark$.subscribe((isDark) => {
      this.toggleTheme(isDark);
    });
    //
    this.searchSubject.pipe(
      debounceTime(350) // ms delay
    ).subscribe(searchTerm => {
      this.onGlobalSearch(searchTerm);
    });

    // Subscribe to the editRow$ Observable (only if not in table-input mode)
    // In table-input mode, the right table should not react to edit events from the left table
    if (!this.isTableInput) {
      this.editRowSubscription = this.tableCommunicationService.editRow$.subscribe((rowIndex: number) => {
        this.editingRows.add(rowIndex);
        this.activeRowEdit = this.editingRows.size > 0;
        this.api.redrawRows();
      });

      this.cancelEditRowSubscription = this.tableCommunicationService.cancelEditRow$.subscribe((rowIndex: number) => {
        this.editingRows.delete(rowIndex);
        this.activeRowEdit = this.editingRows.size > 0;
        this.api.redrawRows();
      });

      this.submitSaveRowSubscription = this.tableCommunicationService.submitSaveRow$.subscribe((rowIndex: number) => {
        this.editingRows.delete(rowIndex);
        this.activeRowEdit = this.editingRows.size > 0;
        this.api.redrawRows();
      });

      this.fullEditModeSubscription = this.tableCommunicationService.fullEditMode$.subscribe(() => {
        this.toggleFullEditMode();
      });
    }

    // MISC. asset popup form
    if (this.tableParams.customButtons && this.tableParams.customButtons.includes('addAsset')) {
      this.getEntityTypeSettings();
      this.getImagerySettings();
    }

    this.tableCommunicationService.event$.subscribe((event: any) => {
      if (event === 'refreshGridViewSettings') {
        this.fetchRowsAndRenderGrid();
      } else if (event === 'reloadDataSource') {
        this.reloadDataSource(null);
      }
    });
    this.isLoading = false;

    this.agGridToolsService.disabledTableActions$.subscribe(value => {
      this.disabledTableActions = value;
    });
    
    // Subscribe to options update events for async options tables
    this.tableCommunicationService.optionsUpdate$.subscribe(() => {
      if (this.shouldDeferFilterApplication) {
        this.applyDeferredFiltersFromURL();
      }
    });
    if (this.assistantEnabled) {
      this.assistantService.buildAssistantContext({
        tableParams: this.tableParams,
        tableRows: null,
        tableMetadata: null,
        agGridTable: this
      });
    }
  }

  closeToolbarSidebar(): void {
    this.isToolbarSidebarOpen = false;
  }

  openToolbarSidebar(): void {
    this.isToolbarSidebarOpen = true;
  }

  onToolbarActionsClick(): void {
    this.actionsDialog?.show();
    this.closeToolbarSidebar();
  }

  get isAgSidePanelOpen(): boolean {
    return (
      this.isColumnsPanelVisible ||
      this.isColumnPresetsPanelVisible ||
      this.isFiltersPanelVisible ||
      this.isSavedFiltersPanelVisible ||
      this.isGridViewSettingsPanelVisible ||
      this.isItemSetsPanelVisible
    );
  }

  get showAgSidePanelCloseButton(): boolean {
    return this.isCompactToolbar && this.isAgSidePanelOpen;
  }

  closeAgSidePanel(): void {
    this.isColumnsPanelVisible = false;
    this.isColumnPresetsPanelVisible = false;
    this.isFiltersPanelVisible = false;
    this.isSavedFiltersPanelVisible = false;
    this.isGridViewSettingsPanelVisible = false;
    this.isItemSetsPanelVisible = false;

    if (this.api) {
      try {
        this.api.closeToolPanel();
        this.api.setSideBarVisible(false);
      } catch (error) {
        console.error('Error closing tool panel:', error);
      }
    }

    this.cdr.markForCheck();
  }

  private updateColumnDefsForViewport(): void {
    this.columnDefs = [...this.baseColumnDefs];

    if (this.columnApi) {
      this.agGridToolsService.applyMobileColumnVisibilityViaApi(
        this.columnApi,
        this.baseColumnDefs,
        this.isMobile,
        this.tableParams,
      );
    }

    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    // Unsubscribe to prevent memory leaks (only if subscriptions exist)
    if (this.duplicateRowSubscription) {
      this.duplicateRowSubscription.unsubscribe();
    }
    if (this.deleteRowSubscription) {
      this.deleteRowSubscription.unsubscribe();
    }
    if (this.editRowSubscription) {
      this.editRowSubscription.unsubscribe();
    }
    if (this.cancelEditRowSubscription) {
      this.cancelEditRowSubscription.unsubscribe();
    }
    if (this.submitSaveRowSubscription) {
      this.submitSaveRowSubscription.unsubscribe();
    }
    if (this.fullEditModeSubscription) {
      this.fullEditModeSubscription.unsubscribe();
    }
    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
    if (this.compactToolbarSubscription) {
      this.compactToolbarSubscription.unsubscribe();
    }
    this.unbindMobileLongPressEdit();
  }

  onGridReady(params: { api: GridApi; columnApi: ColumnApi }) {
    this.api = params.api;
    this.columnApi = params.columnApi;

    // Ensure the grid is properly sized
    setTimeout(() => {
      this.api.refreshCells();
      this.bindMobileLongPressEdit();
    });

    // Apply initial state
    this.applyState();
    
    // Apply any filter from the URL when the grid is ready
    // Check if we should defer filter application for async options tables
    if (this.tableParams?.isAsyncOptions) {
      this.shouldDeferFilterApplication = true;
      this.pendingFilterApplication = true;
    } else {
      this.applyFilterFromURL();
    }
    
    // After the grid is ready, set the custom filter layout
    this.setCustomFilterLayout();

    // Initialize side bar if needed
    if (this.isColumnsPanelVisible || this.isFiltersPanelVisible || this.isItemSetsPanelVisible) {
      this.api.setSideBarVisible(true);
    }
  }

  async onPaginationChanged(event: any) {
    if (!this.api) return;

    let newPage = this.api.paginationGetCurrentPage();

    if (newPage < this.currentPage && !this.isGridViewVisible) {
      this.fetchRowsAndRenderGrid();
    }
    this.currentPage = newPage;
  }

  onColumnVisible(event: any) {
    // Capture and save the new column visibility state
    if (event.source !== "api") {
      this.debounceSaveColumnState();
    }
  }

  onColumnMoved(event: any) {
    // Capture and save the new column order
    if (event.source !== "api") {
      this.debounceSaveColumnState();
      this.setCustomFilterLayout();
    }
  }

  onColumnResized(event: any) {
    // Capture and save the new column order
    if (event.source !== "api") {
      this.debounceSaveColumnState();
    }
  }

  onSortChanged(event: any) {
    // Capture and save the new column order
    if (event.source !== "api") {
      // User chose their own sort, so clearing a Start filter shouldn't override it
      this.isStartSortAutoApplied = false;
      const newColumnState = this.columnApi.getColumnState();
      const sortedColumns = newColumnState.filter(column => column.sort !== null && column.colId !== 'id');
      this.sortCounter = sortedColumns ? sortedColumns.length : null;
      if (sortedColumns.length > 3) {
        // Find the last sorted column
        const lastSortedColumn = sortedColumns[sortedColumns.length - 1];
        // Set the column's sort state to 'null'
        const originalColumn = this.columnState.find((column: { colId: string; }) => column.colId === lastSortedColumn.colId);
        if (originalColumn) {
          originalColumn.sort = null;
          this.columnApi.applyColumnState({
            state: this.columnState,
            applyOrder: true,
          });
          this.messageService.add(
            {
              severity: 'warn',
              summary: 'Maximum (3) Sort Columns Reached',
              detail: 'Column cannot be sorted because the maximum has been reached.',
              life: 1000
            });
        }
      } else {
        // Save the new column state
        this.columnState = newColumnState;
        this.debounceSaveColumnState();
      }
    }
  }

  onCellValueChanged(event: any) {
    console.log('onCellValueChanged', event);
    const recordId = event.data.id;  // Use the record's ID instead of rowIndex
    const field = event.column.getColId();
    const newValue = event.newValue;
    const colDef = event.column.getUserProvidedColDef();

    // Store the original cell value if this is the first change for the cell
    const originalCellKey = `${recordId}-${field}`;
    if (!this.originalData.has(originalCellKey)) {
      this.originalData.set(originalCellKey, event.oldValue);
    }

    // Initialize the map for the row if not already present
    if (!this.changedRows.has(recordId)) {
      this.changedRows.set(recordId, {});
    }

    // Update the changed value for the field
    const rowChanges = this.changedRows.get(recordId);
    rowChanges[field] = this.agGridEditService.convertValueForDB(field, newValue, colDef);
    this.changedRows.set(recordId, rowChanges);
  }

  applyState() {
    // A grid that borrows an entity's columns for a different purpose (e.g. reverse
    // image results, ordered by similarity) must not inherit that entity table's saved
    // sort — any sort replaces the server's relevance order.
    if (this.tableParams?.ignoreSavedColumnState) {
      this.agGridToolsService.applyMobileColumnVisibilityViaApi(
        this.columnApi,
        this.baseColumnDefs,
        this.isMobile,
        this.tableParams,
      );
      this.setCustomFilterLayout();
      return;
    }

    this.agGridUserSettings.getColumnState(this.entity, this.isViewEmbedded, this.parentViewName).then((response: any) => {
      // Apply the state to the grid.
      // Assuming state is an array of column states
      if (response[0] && response[0].settings) {
        this.columnState = this.agGridToolsService.sanitizeColumnState(response[0].settings);
        this.columnApi.applyColumnState({
          state: this.columnState,
          applyOrder: true,
        });
      }
      this.agGridToolsService.applyMobileColumnVisibilityViaApi(
        this.columnApi,
        this.baseColumnDefs,
        this.isMobile,
        this.tableParams,
      );
      this.setCustomFilterLayout();
    }).catch((error) => {
      console.error('Error fetching column state: ', error);
      this.agGridToolsService.applyMobileColumnVisibilityViaApi(
        this.columnApi,
        this.baseColumnDefs,
        this.isMobile,
        this.tableParams,
      );
    });
  }

  debounceSaveColumnState() {
    // Saving would overwrite the real entity table's layout with this grid's.
    if (this.tableParams?.ignoreSavedColumnState) return;

    // Clear any existing timeout to reset the waiting time
    clearTimeout(this.saveColumnStateTimeout);
    this.columnState = this.agGridToolsService.sanitizeColumnState(
      this.columnApi.getColumnState(),
    );
    // Set a new timeout to save the column state after 500 milliseconds (or your preferred time)
    this.saveColumnStateTimeout = setTimeout(() => {
      this.agGridUserSettings.saveColumnState(this.entity, this.columnState, this.isViewEmbedded, this.parentViewName);
    }, 800);
  }

  async onSelectionChanged(event: any) {

    if (this.api) {
      const filterModel = this.api.getFilterModel();
      this.filterCounter = filterModel ? Object.keys(filterModel).length : null;
    }

    const source = event.source;
    const type = event.type;

    if (source == 'api') return;
    if (source == 'uiSelectAll' && type == 'selectionChanged') {
      this.isSelectAll = !this.isSelectAll;
      if (this.isSelectAll) {
        this.lastQueryMatchingIds.forEach((id) => {
          this.selectedRowsSet.add(id);
        });
        this.api.forEachNode((node) => {
          node.setSelected(true);
        });
        this.rowsSelected = this.api.getSelectedNodes();
      } else {
        this.selectedRowsSet.clear();
        this.api.forEachNode((node) => {
          node.setSelected(false);
        });
        this.rowsSelected = [];
      }

      this.areRowsSelected = this.isSelectAll && this.selectedRowsSet.size > 0;

    }
  }

  onRowSelected(event: any) {

    const source = event.source;
    const type = event.type;
    const rowData = event.data;
    const nodeData = event.node;

    if (source == 'uiSelectAll') {
      return;
    }

    if (nodeData.selected) {
      this.selectedRowsSet.add(rowData.id);
    } else {
      this.isSelectAll = false;
      this.selectedRowsSet.delete(rowData.id);
    }

    this.areRowsSelected = this.selectedRowsSet.size > 0;
    this.rowsSelected = this.api.getSelectedNodes();
  }


  // When receiving new data (e.g., after filtering or loading), update the loaded and total row counts:
  updateRowCounts(response: any) {
    this.loadedRowCount += response.records.length; // Increment the loaded row count
    this.totalRowCount = response.paginationMeta.totalRecords; // Update the total row count
    this.tableTotalRecords = response.paginationMeta.tableTotalRecords;
    this.lastQueryMatchingIds = new Set(response.matchingIds);
    this.tableCommunicationService.setRowCount(this.totalRowCount);
  }

  resetLoadedRowCount() {
    this.loadedRowCount = 0;
  }

  onResetRows(isClear: boolean = false) {
    // Deselect all rows
    this.api.deselectAll();
    this.isSelectAll = false;
    // Update the selected row count
    if (isClear) {
      this.selectedRowsSet.clear();
    }

    this.areRowsSelected = false;
    this.rowsSelected = [];
  }


  onSearchInputChange() {
    this.searchSubject.next(this.globalSearchTerm);
  }

  /**
   * AI tag search (Items and Challenges only).
   *
   * Reuses the same debounced subject as the plain box; only the query built in
   * getGlobalSearchQuery() differs, and that branches on aiSearchMode.
   */
  onAiSearchChange(change: AiTableSearchChange) {
    this.globalSearchTerm = change.term;
    this.aiSearchMode = change.mode;
    this.searchSubject.next(this.globalSearchTerm);
  }

  onGlobalSearch(searchTerm: string) {
    // Handle the global search here
    this.globalSearchFilter = searchTerm;
    this.api.onFilterChanged(); // This will trigger the getRows method with the new filter
  }

  async setMeta(params: any) {
    const startRow = params.request.startRow ?? 0;
    const pageSize = this.gridOptions.cacheBlockSize ?? 100;
    const currentPage = Math.floor(startRow / pageSize);
    const sortModel = params.request.sortModel;
    const filterModel = params.request.filterModel;

    // Simplified populate configuration
    const populate = this.agGridToolsService.buildPopulateConfig(params.columnApi);
    this.populateFields = populate;

    // Handle global search
    let tableGlobalSearchQuery = null;
    if (this.globalSearchFilter.length > 0) {
      tableGlobalSearchQuery = this.getGlobalSearchQuery(this.globalSearchFilter);
    }

    // Build filter model
    const columnKeys = Object.keys(filterModel);
    const filterParamsDict = columnKeys.reduce((acc: any, key: any) => {
      const colDef = params.columnApi.getColumn(key)?.getColDef();
      acc[key] = { ...filterModel[key], ...colDef?.filterParams };
      return acc;
    }, {});

    return {
      pageSize,
      currentPage,
      sortModel,
      filterModelVars: {
        filterModel,
        filterParamsDict,
        columnKeys
      },
      populate,
      tableGlobalSearchQuery
    };
  }


  createServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: async (params) => {
          await this.handleSingleEntity(params);
        
        this.api.forEachNode((node, index) => {
          if (this.selectedRowsSet.has(node.data?.id)) {
            node.setSelected(true);
          }
        });
      }
    }
  }

  async handleSingleEntity(params: any) {
    const { pageSize, currentPage, sortModel, filterModelVars, populate, tableGlobalSearchQuery } = await this.setMeta(params)

    // Check if the query has changed
    if (!isEqual(filterModelVars.filterModel, this.previousFilterModel)) {
      this.resetLoadedRowCount(); // Reset the loaded row count
      this.previousFilterModel = filterModelVars.filterModel; // Update the previous query
    }

    if (this.tableParams && this.tableParams.hideActions) {
      this.hideActions = this.tableParams.hideActions
    }

    let payloadStoreEnv: string | null = null;
    if (this.isStoreEntity) {
      let storeEnv: any = await this.agGridToolsService.getSelectedEnvironment()
      payloadStoreEnv = storeEnv.value
    }

    this.currentSortModel = sortModel;
    this.currentFilterModelVars = filterModelVars;

    // Check if custom fetch function is provided
    if (this.tableParams?.customFetchFunction) {
      try {
        const response = await this.tableParams.customFetchFunction({
          page: currentPage,
          pageSize: pageSize,
          sortModel: sortModel || null,
          filterModel: filterModelVars || null,
          populate: populate,
          tableGlobalSearchQuery: tableGlobalSearchQuery || null,
          defaultQuery: this.tableParams.defaultQuery || null,
        });
        
        this.loadingGrid = false;
        this.mongoQuery = response.query || {};
        this.mongoSort = response.sort || {};

        if (response.records) {
          this.updateRowCounts(response);
          params.success({
            rowData: response.records,
            rowCount: response.paginationMeta.totalRecords
          });
        } else {
          params.fail();
        }
      } catch (error) {
        this.loadingGrid = false;
        console.error('Error in custom fetch function:', error);
        params.fail();
      }
      return;
    }

    let response = await this.tableService.getTableRowsV3({
      type: this.entity,
      // TODO What to do with autopopulateSelect ?
      autopopulate: { select: this.autopopulateSelect },
      populate: populate,
      page: currentPage,
      pageSize: pageSize,
      // TODO What to do with nestedPopulate ?
      nestedPopulate: this.nestedPopulate,
      storeEnv: payloadStoreEnv,
      sortModel: sortModel || null,
      filterModel: filterModelVars || null,
      defaultQuery: this.tableParams.defaultQuery || null,
      tableGlobalSearchQuery: tableGlobalSearchQuery || null,
      entities: this.tableParams.entities || null,
      select: this.tableParams.select || null,
      truncateFields: this.tableParams.truncateFields || null,
      isPaginatedDynamicEntity: this.isDynamicEntity || false,
    });

    this.loadingGrid = false;
    this.mongoQuery = response.query;
    this.mongoSort = response.sort;

    if (response.records) {

      // Update the loaded and total row counts
      if (!this.isGridViewVisible) {
        this.fetchRowsAndRenderGrid(response.records);
      }

      this.updateRowCounts(response);
      this.api.retryServerSideLoads();

      // Notify the grid of the results
      params.success({
        rowData: response.records, // Assuming the response contains the row data under 'records' property
        rowCount: response.paginationMeta.totalRecords // Total number of records across all pages
      });

    } else {
      // inform grid request failed
      console.error('IServerSideDatasource failed');
      params.fail();
    }
  }


  /**
   * Asynchronously fetches all rows from the backend service, with options for field selection and smart population.
   *
   * This function utilizes the `tableService.getBulkActionRows` method to get all records from the backend.
   * Pagination might be necessary if the dataset is large.
   *
   * @param {string} select - Specifies which fields to include in the results, defaults to '_id id'.
   *                          It is a string with space-separated field names.
   *
   * @param {any} smartPopulate - An object containing advanced options for populating related fields,
   *                              defaults to an empty object.
   *
   * @returns {Promise<any[]>} A Promise that resolves with an array of all fetched records.
   *
   * @async
   * @example
   *
   * const data = await fetchAllRowsFromBackend('_id name', { populate: 'friends' });
   */
  async fetchAllRowsFromBackend(select: string = '_id id', selectedRowsSet: Set<number> | null = null, props: any = {}, isSelectAll: boolean = false): Promise<any[]> {
    let allData = await this.tableService.getTableRowsV3({
      type: this.entity,
      select: select,
      autopopulate: props.autopopulate || false,
      populate: props.populate || null,
      sortModel: this.currentSortModel || null,
      filterModel: this.currentFilterModelVars || null,
      defaultQuery: this.tableParams.defaultQuery || null,
      selectedRows: selectedRowsSet ? Array.from(selectedRowsSet) : null,
      page: 0,
      pageSize: isSelectAll ? 30000 : (selectedRowsSet?.size || 50),
      isPaginatedDynamicEntity: this.isDynamicEntity,
    });

    return allData.records;
  }

  getGlobalSearchQuery(searchFilter: string): any {
    if (!searchFilter) return {};

    // Items and Challenges search the AI tags, object tags and description through a
    // single $text query. Terms are ANDed there, unlike the $or below — see
    // buildAiTagQuery for why the two cannot be combined into one branch.
    if (this.tableParams?.aiSearch) {
      return this.aiSearchMode === 'ai'
        ? buildAiTagQuery(searchFilter)
        // Same name/fileName search as every other grid, but with the user's input
        // escaped before it reaches $regex — an unescaped "(" throws server-side.
        : buildFallbackQuery(searchFilter, this.tableParams?.tableSearchColumns || []);
    }

    // Initialize search conditions with the default 'name' field
    const searchConditions: any = [{ name: { $regex: searchFilter, $options: 'i' } }];

    // Distinguish between numeric and non-numeric filters more cleanly
    if (isNaN(Number(searchFilter))) {
      // Directly map tableSearchColumns to search conditions if applicable
      if (this.tableParams?.tableSearchColumns) {
        searchConditions.push(...this.tableParams.tableSearchColumns.map((field: any) => ({
          [field]: { $regex: searchFilter, $options: 'i' }
        })));
      }
    } else {
      // Handle numeric search filter, applying it to the 'id' field
      searchConditions.push({ id: Number(searchFilter) });
    }

    return { $or: searchConditions };
  }

  parse(value: string): string {

    if (this.tableParams && this.tableParams.customName) {
      return this.tableParams.customName;
    }

    // Replace underscores with spaces
    value = value.replace(/_/g, ' ');

    // Split the string into words based on spaces and hyphens
    const words = value.split(/[-\s]/);

    // Capitalize the first letter of each word and join them back together with a space
    return words.map(word => {
      // Capitalize the first letter and concatenate with the rest of the word
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');

  }

  toggleTheme(isDarkMode: boolean) {
    this.gridTheme = isDarkMode ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';
  }

  // URL filters management
  // Function to fetch table filter state, generate URL param, and apply to URL
  updateURLWithFilter() {
    const currentFilterModel = this.api.getFilterModel();
    const filterString = JSON.stringify(currentFilterModel);
    const compressedFilter = LZString.compressToEncodedURIComponent(filterString);
    this.onResetRows();
    this.syncStartDateFilterSort(currentFilterModel);
    // Grids that share a page (or show a transient result set) keep their filters to
    // themselves: the URL is one slot, so a second grid would read this filter as its
    // own, and a later result set would inherit a stale one.
    if (this.tableParams?.ignoreUrlFilters) return;
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: { filter: compressedFilter },
        // queryParamsHandling: 'merge',
        replaceUrl: true,
      });
  }

  /**
   * Filtering by Start date while still on the default ID sort returns rows in creation order,
   * so switch to Start ascending to list them chronologically. Reverts to the default ID sort
   * when the filter is cleared, unless the user picked their own sort in the meantime.
   */
  syncStartDateFilterSort(filterModel: any) {
    const hasStartFilter = !!filterModel?.start;

    if (hasStartFilter && !this.isStartSortAutoApplied && this.columnApi?.getColumn('start')) {
      const sortedColumns = this.columnApi.getColumnState().filter(column => column.sort);
      const isDefaultSort = sortedColumns.length === 1 && sortedColumns[0].colId === 'id' && sortedColumns[0].sort === 'desc';
      if (isDefaultSort) {
        this.columnApi.applyColumnState({
          state: [{ colId: 'start', sort: 'asc', sortIndex: 0 }],
          defaultState: { sort: null },
        });
        this.isStartSortAutoApplied = true;
      }
    } else if (!hasStartFilter && this.isStartSortAutoApplied) {
      this.columnApi.applyColumnState({
        state: [{ colId: 'id', sort: 'desc', sortIndex: 0 }],
        defaultState: { sort: null },
      });
      this.isStartSortAutoApplied = false;
    }
  }

  // Function to apply URL filters after async options are loaded
  applyDeferredFiltersFromURL() {
    console.log('applyDeferredFiltersFromURL');
    if (!this.pendingFilterApplication) {
      return;
    }
    this.pendingFilterApplication = false;
    this.applyFilterFromURL();
  }

  // Function to decompress URL param and apply to table state when loading
  applyFilterFromURL() {
    if (this.tableParams?.ignoreUrlFilters) return;
    this.route.queryParams.subscribe(params => {
      const customFilters: { [key: string]: string } = Object.keys(params).filter((paramKey) => paramKey !== 'filter').reduce((acc: any, currentKey: string) => {
        acc[currentKey] = params[currentKey];
        return acc;
      }, {});
      const compressedFilter = params['filter'];

      if (compressedFilter) {
        const decompressedFilterString = LZString.decompressFromEncodedURIComponent(compressedFilter);
        const filterModel = JSON.parse(decompressedFilterString);
        this.api.setFilterModel(filterModel);
      } else if (Object.keys(customFilters).length > 0) {
        const filterModel: any = {};

        for (const [filterName, value] of Object.entries(customFilters)) {
          const filterType = this.getFilterType(filterName);
          const newFilterModel = filterType && this.buildFilterModel(filterType, value);
          if (newFilterModel) {
            filterModel[filterName] = newFilterModel;
          }
        }
        
        if (Object.keys(filterModel).length > 0) {
          this.api.setFilterModel(filterModel);
        }
      }
    });
  }

  getFilterType(field: string): string | null {
    const columnDef: any = this.gridOptions.columnDefs?.find((col: any) => col.field === field);
    return columnDef?.filter || null;
  }

  buildFilterModel(filterType: string, value: string): any {
    switch (filterType) {
      case 'agNumberColumnFilter':
        return { filter: Number(value), type: 'equals' };
      default:
        return null;
    }
  }

  // EDIT
  isCellEditable(params: any) {
    // Partial (truncated) list values must never be saved back
    if (params.data?._truncatedFields?.includes(params.colDef?.field)) {
      return false;
    }
    // If full edit mode is on, return true
    if (this.isFullEditMode) {
      return true;
    }
    const rowIndex = params.node.rowIndex;
    return this.editingRows.has(rowIndex);
  }

  /**
   * Mobile/tablet: long-press a cell to enter row edit and open that cell's editor.
   * Finger movement cancels the press so scrolling still works.
   */
  private bindMobileLongPressEdit(): void {
    this.unbindMobileLongPressEdit();
    const host = this.agGridHost?.nativeElement;
    if (!host) {
      return;
    }

    const onTouchStart = (event: TouchEvent) => this.onLongPressTouchStart(event);
    const onTouchMove = (event: TouchEvent) => this.onLongPressTouchMove(event);
    const onTouchEnd = () => this.clearLongPress();
    const onContextMenu = (event: Event) => {
      if (this.isCompactToolbar && this.longPressOrigin) {
        event.preventDefault();
      }
    };

    host.addEventListener('touchstart', onTouchStart, { passive: true });
    host.addEventListener('touchmove', onTouchMove, { passive: true });
    host.addEventListener('touchend', onTouchEnd, { passive: true });
    host.addEventListener('touchcancel', onTouchEnd, { passive: true });
    host.addEventListener('contextmenu', onContextMenu);

    this.longPressUnlisteners = [
      () => host.removeEventListener('touchstart', onTouchStart),
      () => host.removeEventListener('touchmove', onTouchMove),
      () => host.removeEventListener('touchend', onTouchEnd),
      () => host.removeEventListener('touchcancel', onTouchEnd),
      () => host.removeEventListener('contextmenu', onContextMenu),
    ];
  }

  private applyWritePermissions(): void {
    const controller = this.tableParams?.permissionController;
    if (!controller) return;

    const apply = () => {
      this.canCreate = this.authService.hasPermission(['create'], controller);
      this.canUpdate = this.authService.hasPermission(['update'], controller);
      this.canDelete = this.authService.hasPermission(['delete'], controller);
    };
    // Synchronous on purpose: awaiting here, ahead of the rest of ngOnInit, left the grid without
    // a datasource. AuthGuard has always loaded the permissions by now; a table it didn't guard
    // loads them in the background, and keeps its controls if that fails (the API still rejects).
    if (this.authService.permissions) {
      apply();
    } else {
      this.authService.loadPermissions().pipe(take(1)).subscribe({ next: apply, error: () => {} });
    }
  }

  private unbindMobileLongPressEdit(): void {
    this.clearLongPress();
    this.longPressUnlisteners.forEach((unlisten) => unlisten());
    this.longPressUnlisteners = [];
  }

  private onLongPressTouchStart(event: TouchEvent): void {
    if (!this.isCompactToolbar || !this.api || !this.canUpdate || this.authService.hasRole('ROLE_READ_ONLY')) {
      return;
    }
    if (event.touches.length !== 1) {
      this.clearLongPress();
      return;
    }

    const touch = event.touches[0];
    const cell = this.getEditableCellFromTouchTarget(event.target);
    if (!cell) {
      return;
    }

    this.longPressOrigin = {
      x: touch.clientX,
      y: touch.clientY,
      rowIndex: cell.rowIndex,
      colId: cell.colId,
    };

    this.longPressTimer = setTimeout(() => {
      const origin = this.longPressOrigin;
      this.clearLongPress();
      if (!origin) {
        return;
      }
      this.beginLongPressCellEdit(origin.rowIndex, origin.colId);
    }, this.LONG_PRESS_MS);
  }

  private onLongPressTouchMove(event: TouchEvent): void {
    if (!this.longPressOrigin || event.touches.length !== 1) {
      return;
    }
    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - this.longPressOrigin.x);
    const dy = Math.abs(touch.clientY - this.longPressOrigin.y);
    if (dx > this.LONG_PRESS_MOVE_TOLERANCE_PX || dy > this.LONG_PRESS_MOVE_TOLERANCE_PX) {
      this.clearLongPress();
    }
  }

  private clearLongPress(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    this.longPressOrigin = null;
  }

  private getEditableCellFromTouchTarget(
    target: EventTarget | null,
  ): { rowIndex: number; colId: string } | null {
    const el = target as HTMLElement | null;
    if (!el?.closest) {
      return null;
    }

    const cellEl = el.closest('.ag-cell') as HTMLElement | null;
    const rowEl = el.closest('.ag-row') as HTMLElement | null;
    if (!cellEl || !rowEl) {
      return null;
    }

    const colId = cellEl.getAttribute('col-id');
    const rowIndexAttr = rowEl.getAttribute('row-index');
    if (!colId || rowIndexAttr == null) {
      return null;
    }

    const rowIndex = Number(rowIndexAttr);
    if (Number.isNaN(rowIndex) || !this.isColumnLongPressEditable(colId)) {
      return null;
    }

    return { rowIndex, colId };
  }

  private isColumnLongPressEditable(colId: string): boolean {
    if (!colId || colId === 'actions' || colId.startsWith('ag-Grid-')) {
      return false;
    }

    const column = this.columnApi?.getColumn(colId);
    if (!column || !column.isVisible()) {
      return false;
    }

    const def = column.getColDef();
    const types = Array.isArray(def.type) ? def.type : def.type ? [def.type] : [];
    if (types.includes('editableColumn')) {
      return true;
    }
    return def.editable === true;
  }

  private beginLongPressCellEdit(rowIndex: number, colId: string): void {
    this.zone.run(() => {
      if (!this.api || !this.canUpdate || this.authService.hasRole('ROLE_READ_ONLY')) {
        return;
      }

      this.editingRows.add(rowIndex);
      this.activeRowEdit = true;
      this.updateColumnDefsForViewport();

      const rowNode = this.api.getDisplayedRowAtIndex(rowIndex);
      if (rowNode) {
        this.api.redrawRows({ rowNodes: [rowNode] });
      } else {
        this.api.redrawRows();
      }

      // Wait a frame so editable flags apply before opening the editor
      requestAnimationFrame(() => {
        try {
          this.api.startEditingCell({ rowIndex, colKey: colId });
        } catch (error) {
          console.error('Long-press cell edit failed:', error);
        }
        this.cdr.markForCheck();
      });
    });
  }


  async submitFullEdit() {
    // Stop any cell that is currently being edited and commit its value
    this.api.stopEditing();
    
    // Wait a tick to ensure all cell value changes have been processed
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Get all record IDs of changed rows
    const changedRecordIds = Array.from(this.changedRows.keys());

    console.log('submitFullEdit - record IDs:', changedRecordIds);

    // Initialize an array to store the promises
    const promises = [];
    // Loop through each record ID and call submitRow
    for (const recordId of changedRecordIds) {
      let storeEnv: any = { value: null };
      if (this.isStoreEntity) {
        storeEnv = this.agGridToolsService.getSelectedEnvironment();
      }
      const promise = this.agGridEditService.submitRow(recordId, this.changedRows, this.api, this.entity, null, null, storeEnv?.value, this.isDynamicEntity);
      promises.push(promise);
    }
    // Wait for all promises to resolve
    try {
      await Promise.all(promises);
      // All rows have been submitted successfully
      this.isFullEditMode = false;
      // submitRow() fires submitSaveRow$ per changed row, which recomputes
      // activeRowEdit from the still-populated full-edit editingRows set and
      // can leave it stuck true. Reset it explicitly on exit.
      this.activeRowEdit = false;
      this.toggleFullEditMode();
    } catch (error) {
      // Handle error
      console.error("An error occurred while submitting rows:", error);
    }
  }

  revertChanges(changedRecordIds: number[]) {
    this.agGridEditService.revertChanges(this.api, this.changedRows, this.originalData, changedRecordIds);
  }

  async toggleFullEditMode() {
    // If full edit mode is turned on, add all visible rows to the editingRows set
    if (this.isFullEditMode) {
      const visibleRows = this.api.getModel().getRowCount();
      for (let i = 0; i < visibleRows; i++) {
        this.editingRows.add(i);
      }
      this.api.redrawRows();
      return; // exit the function
    }

    // Commit any cell that is still being edited so its value lands in
    // changedRows before we check for unsaved changes. Because
    // stopEditingWhenCellsLoseFocus is false, clicking the toggle does not
    // auto-commit the open editor.
    this.api.stopEditing();

    // Cell value changes propagate to changedRows asynchronously (via
    // onCellValueChanged), so wait a tick after stopEditing before checking —
    // otherwise the first toggle-off reads an empty changedRows and skips the
    // confirmation popup. Same reason submitFullEdit() waits here.
    await new Promise(resolve => setTimeout(resolve, 100));

    // If toggled off and there are changed rows not submitted
    const changedRecordIds = Array.from(this.changedRows.keys());
    if (changedRecordIds.length > 0) {
      this.confirmationService.confirm({
        message: 'There are unsaved changes. Do you want to submit them?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          // If user chooses to submit
          this.submitFullEdit();
          this.editingRows.clear();
          this.activeRowEdit = false;
        },
        reject: () => {
          // If user chooses to discard
          // Here you can revert the changes if necessary
          this.revertChanges(changedRecordIds);
          this.editingRows.clear();
          this.activeRowEdit = false;
          this.api.redrawRows();
        }
      });
    } else {
      this.editingRows.clear();
      this.activeRowEdit = false;
      this.api.redrawRows();
    }
  }

  /**
  * Retrieves data related to current user
  */
  getUserData() {
    // get current user
    const userResult = this.authService.getSocialUser();
    this.userParams.user = userResult.currentUser;
  }

  //  Custom filters
  applyCustomFilter(filterType: string) {
    switch (filterType) {
      case 'archived':
        // Toggle the current state
        this.showArchived = !this.showArchived;

        // Prepare the filter model for the "archived" column
        const filterModel = this.showArchived ? null : {
          values: ['false'],
          filterType: 'set'
        };

        // Apply the filter to the "archived" column
        this.api.setFilterModel({
          archived: filterModel
        });

        // Refresh the rows based on the new filter
        this.api.onFilterChanged();
        break;

      // Handle other custom filters here...
      default:
        break;
    }
  }

  showAssetDialog() {
    this.displayAssetDialog = true;
  }

  showStringDialog() {
    this.displayStringDialog = true;
  }

  showImportCsvDialog() {
    this.displayImportCsvDialog = true;
    this.importCsvErrors = [];
    this.importCsvSelectedFileName = null;
    this.importCsvSelectedFile = null;
    this.importCsvSkipExisting = true;
    this.importCsvTriggerTranslation = true;
  }

  hideImportCsvDialog() {
    this.displayImportCsvDialog = false;
    this.importCsvErrors = [];
    this.importCsvSelectedFileName = null;
    this.importCsvSelectedFile = null;
  }

  downloadClientStringsCsvTemplate() {
    this.http.get('/api/localization/client-strings/csv-template', { responseType: 'text' }).subscribe({
      next: (csv) => {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'client-strings-import-template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Download failed', detail: 'Could not download template.' }),
    });
  }

  onImportCsvNativeChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.importCsvSelectedFile = file;
    this.importCsvSelectedFileName = file.name;
    this.importCsvErrors = [];
    // Reset the input so the same file can be re-selected after an error
    input.value = '';
  }

  clearImportCsvFile() {
    this.importCsvSelectedFile = null;
    this.importCsvSelectedFileName = null;
    this.importCsvErrors = [];
  }

  runImportCsv() {
    const file = this.importCsvSelectedFile;
    if (!file || this.importCsvUploading) return;
    this.importCsvUploading = true;
    this.importCsvErrors = [];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('triggerTranslation', this.importCsvTriggerTranslation ? 'true' : 'false');
    formData.append('skipExisting', this.importCsvSkipExisting ? 'true' : 'false');
    this.http.post<
      { success: true; created: number; updated: number; skipped: number; fields: string[]; triggerTranslation: boolean } |
      { success: false; errors: Array<{ row: number; message: string }> }
    >('/api/localization/client-strings/import-csv', formData).subscribe({
      next: (res) => {
        this.importCsvUploading = false;
        if (res.success) {
          this.displayImportCsvDialog = false;
          this.importCsvSelectedFile = null;
          this.importCsvSelectedFileName = null;
          this.importCsvErrors = [];
          this.api?.refreshServerSide?.();
          const parts: string[] = [];
          if (res.created > 0) parts.push(`${res.created} created`);
          if (res.updated > 0) parts.push(`${res.updated} updated`);
          if (res.skipped > 0) parts.push(`${res.skipped} skipped (already existed)`);
          const summary = parts.join(', ') || 'No changes made';
          this.messageService.add({
            severity: res.created > 0 || res.updated > 0 ? 'success' : 'info',
            summary: `Import complete — ${summary}`,
            detail: res.triggerTranslation && (res.created + res.updated) > 0
              ? 'AI translation has been queued for new/updated records.'
              : '',
            life: 8000,
          });
        } else {
          this.importCsvErrors = res.errors || [];
        }
      },
      error: (err) => {
        this.importCsvUploading = false;
        const msg = err?.error?.message || err?.message || 'Import failed. Please try again.';
        this.importCsvErrors = [{ row: 0, message: msg }];
      },
    });
  }

  hideAssetDialog() {
    this.displayAssetDialog = false;
  }

  hideStringDialog() {
    this.displayStringDialog = false;
    this.api.redrawRows();
  }

  /**
   * Retrieves all Entity Type Settings records.
   */
  async getEntityTypeSettings() {
    let entityTypes = null;
    if (this.tableParams && this.tableParams.entityTypes) {
      entityTypes = this.tableParams.entityTypes;
    } else {
      entityTypes = null;
    }
    let response = await this.gettersService.getEntityTypeSettings(true, false, entityTypes);

    if (response && response.length > 0) {
      this.entityTypeSettings = response;
      this.entityTypeSettings.forEach((entityTypeSetting: any) => {
        // this.AllEntityTypeOptions.push({ label: entityTypeSetting.name, value: entityTypeSetting.value });
        if (!entityTypeSetting.showOnAssetBundleOnly || (this.tableParams && this.tableParams.allEntityTypes) || this.entity === 'ImagerySettings') {
          this.entityTypeOptions.push({ label: entityTypeSetting.name, value: entityTypeSetting.value });
        }
      });

      this.entityTypeOptions.sort((a, b) => a.value - b.value);
    }
    this.entityTypesLoaded = true;
  }

  /**
   * Retrieves all Imagery Settings records.
   */

  async getImagerySettings() {
    let response = await this.gettersService.getBundleTypeSettings(true);
    if (response && response.length > 0) {
      this.imagerySettings = response;

      this.imagerySettings.forEach((imagerySetting: any) => {
        // this.AllImageryOptions.push({ label: imagerySetting.name, value: imagerySetting.value });
      }
      );
      // this.AllImageryOptions.sort((a, b) => a.value - b.value);
    }
    this.imagerySettingsLoaded = true;
  }

  reloadDataSource(event: any) {
    // Refresh the entire dataset from the server
    this.api.refreshServerSide();

    // Reset miscAssetEntity to default values
    this.resetMiscAssetEntity();

    this.hideAssetDialog();
  }

  resetMiscAssetEntity() {
    this.miscAssetEntity = {
      id: null,
      name: '',
      entityType: null,
      assetType: null,
      path: '',
    };
  }

  async syncStore() {
    let response = await this.agGridToolsService.syncStore();
    this.api.refreshServerSide();
  }

  clearFilters() {
    this.api.setFilterModel(null);
  }

  clearSorting() {
    this.columnApi.applyColumnState({
      defaultState: { sort: null }
    });
    this.sortCounter = null;
  }


  toggleGrid() {
    this.zone.run(() => {
      const agGridElement = this.renderer.selectRootElement('.ag-root.ag-unselectable', true);

      if (this.isGridViewVisible) {
        // Hide the ag-grid view
        this.renderer.setStyle(agGridElement, 'visibility', 'hidden');
        this.renderer.setStyle(agGridElement, 'opacity', '0');

        this.fetchRowsAndRenderGrid();

      } else {
        // Show the ag-grid view
        this.renderer.setStyle(agGridElement, 'visibility', 'visible');
        this.renderer.setStyle(agGridElement, 'opacity', '1');

        this.removeGridViewComponent();
      }

      this.isGridViewVisible = !this.isGridViewVisible;
    });
  }


  getCurrentPageData() {

    // Get current page and page size
    let currentPage = this.api.paginationGetCurrentPage();
    let pageSize = this.api.paginationGetPageSize();

    // Calculate the starting and ending index for the current page
    let startRow = currentPage * pageSize;
    let endRow = Math.min(startRow + pageSize, this.api.paginationGetRowCount());

    // Get all the rows in the current page
    let pageData: any[] = [];
    this.api.forEachNode((node, index) => {
      if (index >= startRow && index < endRow) {
        pageData.push(node.data);
      }
    });

    return pageData;
  }


  async updateCurrentPageData() {
    await new Promise(resolve => setTimeout(resolve, 500));
    const data = this.getCurrentPageData();
    this.tableCommunicationService.setCurrentPageData(data);
  }

  showGridViewComponent(currentPageData: any[], agGridElement: { parentNode: { appendChild: (arg0: any) => void; }; }) {
    const factory = this.componentFactoryResolver.resolveComponentFactory(GridViewComponent);
    this.componentRef = factory.create(this.viewContainerRef.injector);
    this.componentRef.instance.rowData = currentPageData;
    this.componentRef.instance.entity = this.entity;

    // Manually trigger change detection
    this.cdr.detectChanges();

    // Append the component to the DOM
    const gridViewElement = this.componentRef.location.nativeElement;
    agGridElement.parentNode.appendChild(gridViewElement);

    if (this.componentRef.instance.ngOnInit) {
      this.componentRef.instance.ngOnInit();
    }
  }

  async removeGridViewComponent() {
    if (this.componentRef) {
      const gridViewElement = this.componentRef.location.nativeElement;

      // Check if the element is still part of the DOM and remove it
      if (gridViewElement.parentNode) {
        gridViewElement.parentNode.removeChild(gridViewElement);
      }

      if (this.componentRef.instance.ngOnDestroy) {
        this.componentRef.instance.ngOnDestroy();
      }

      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  updateGridViewComponentData(newData: any[]) {
    if (this.componentRef) {
      // Update the rowData property of the GridViewComponent
      this.componentRef.instance.rowData = newData;

      // Manually trigger change detection in the GridViewComponent
      this.componentRef.changeDetectorRef.detectChanges();
    }
  }

  // tool panels management functions
  togglePanel(panel: string) {
    if (!this.api) {
      console.warn('AG Grid API not yet initialized');
      return;
    }

    let isVisible;
    switch (panel) {
      case 'columns':
        this.isColumnsPanelVisible = !this.isColumnsPanelVisible;
        isVisible = this.isColumnsPanelVisible;
        break;
      case 'columnPresets':
        this.isColumnPresetsPanelVisible = !this.isColumnPresetsPanelVisible;
        isVisible = this.isColumnPresetsPanelVisible;
        break;
      case 'filters':
        this.isFiltersPanelVisible = !this.isFiltersPanelVisible;
        isVisible = this.isFiltersPanelVisible;
        break;
      case 'cmsFilters':
        this.isSavedFiltersPanelVisible = !this.isSavedFiltersPanelVisible;
        isVisible = this.isSavedFiltersPanelVisible;
        break;
      case 'gridViewSettings':
        this.isGridViewSettingsPanelVisible = !this.isGridViewSettingsPanelVisible;
        isVisible = this.isGridViewSettingsPanelVisible;
        break;
      case 'itemSets':
        this.isItemSetsPanelVisible = !this.isItemSetsPanelVisible;
        isVisible = this.isItemSetsPanelVisible;
        break;
    }

    try {
      if (isVisible) {
        this.api.setSideBarVisible(true);
        this.api.openToolPanel(panel);
      } else {
        this.api.closeToolPanel();
        this.api.setSideBarVisible(false);
      }
    } catch (error) {
      console.error('Error toggling tool panel:', error);
    }

    this.cdr.markForCheck();
  }

  setCustomFilterLayout() {
    // Fetch the original column definitions
    const originalColDefs = this.columnApi.getAllGridColumns().map(col => col.getColDef());

    // Sort the column definitions alphabetically based on headerName
    const sortedColDefs = [...originalColDefs].sort((a: any, b: any) => {
      return a.headerName.localeCompare(b.headerName);
    });

    // Obtain the Filters Tool Panel instance
    const filtersToolPanel = this.api.getToolPanelInstance('filters');

    // Apply the custom filter layout
    if (filtersToolPanel) {
      filtersToolPanel.setFilterLayout(sortedColDefs as ColDef[]);
    }
  }

  // STORE RELATED FUNCTIONS:
  async addToCart(type: string) {
    this.tableCommunicationService.addToCart(type)
  }

  async addSelectedData() {
    if (this.isTableInput) {
      let extraProps = {};
      if (this.tableInputParams?.populate) {
        extraProps = { populate: this.tableInputParams.populate }
      }
      const data = await this.fetchAllRowsFromBackend(this.tableInputParams?.select || 'id name', this.selectedRowsSet, extraProps);
      this.selectionChange.emit(data);
      console.log('Selected data', data);
      this.selectedRows = data;

      // Show a toast message
      this.messageService.add({
        severity: 'success',
        summary: 'Items Added',
        detail: `Added ${this.selectedRowsSet.size} items.`,
      });

      this.api.deselectAll();

    }
  }

  showDuplicateDialog(payload: any) {
    this.dupDialogComponent.show(payload)
  }

  private isEventForThisTable(event: any): boolean {
    return !!event?.sourceTableId && event.sourceTableId === this.tableInstanceId;
  }

  handleDuplicationSuccess(event: any) {
    this.reloadDataSource(event);
  }

  getGridHeight() {
    if (this.customHeight) {
      return this.customHeight;
    } else {
      const height = window.innerHeight;
      if (height >= 1000) {
        return '85vh';
      } else if (height >= 900) {
        return '83vh';
      } else if (height >= 800) {
        return '78vh';
      } else if (height >= 700) {
        return '75vh';
      } else {
        return '70vh';
      }
    }
  }

  async fetchRowsAndRenderGrid(rows: any[] | null = null) {
    if (!rows) {
      rows = this.getCurrentPageData();
    }
    this.loadingGrid = true;
    this.tableCommunicationService.emitRefreshGrid(rows);
    await this.removeGridViewComponent();
    this.showGridViewComponent(rows, this.renderer.selectRootElement('.ag-root.ag-unselectable', true));
    this.loadingGrid = false;
  }

  async validateDeletion(payload: any, env: any): Promise<any> {
    return await this.tableService.validateDeletion({...payload, entityType: this.entity, env: env ? env.value : null})
  }

  async handleRowDeletion(event: any) {
    let env;
    if(this.entity == 'price-points') {
      env = this.agGridToolsService.getSelectedEnvironment();
    }
    const valid = await this.validateDeletion(event, env);

    if (valid.ids && valid.ids.length === 0) {
      const result = await this.tableService.executeDeletion({ ...event, entityType: this.entity });

      if (result.success) {
        this.dialogData = {
          header: 'Deletion Successful',
          content: 'The record has been successfully deleted from the current environment.',
          footerLabel: 'OK'
        };
      } else {
        this.dialogData = {
          header: 'Deletion Error',
          content: `There was an error while deleting the record: ${result.error}`,
          footerLabel: 'Close'
        };
      }
    } else {
      this.dialogData = {
        header: 'Cannot Delete Record',
        content: `This record is being referenced in the following entity: ${valid.entity}.`,
        ids: valid.ids,
        footerLabel: 'Understood'
      };
      
    }

  this.dialogVisible = true; // Show the dialog
  }

  closeDialog() {
    this.dialogVisible = false;  // Hide the modal by setting dialogData to null
  }

  async showDynamicDialog(): Promise<void> {
    // Load entity types if this is ImagerySettings and they haven't been loaded yet
    if (this.entity === 'ImagerySettings' && !this.entityTypesLoaded) {
      await this.getEntityTypeSettings();
    }

    // Generate form fields based on column definitions
    this.dynamicFormFields = this.columnDefs
      .filter(col => {
        // First check if field should be excluded
        if (this.excludeFields.includes(col.field as string)) {
          return false;
        }

        // If includeFields is specified, only include those fields
        if (this.includeFields.length > 0 && !this.includeFields.includes(col.field as string)) {
          return false;
        }

        // Apply default filters
        return !col.hide && col.field !== 'actions' && col.field !== 'id';
      })
      .map(col => {
        const field: any = {
          key: col.field,
          label: col.headerName || col.field,
          type: this.getFieldType(col),
          placeholder: `Enter ${(col.headerName || col.field as string).toLowerCase()}`
        };

        // Add validation if required
        if (col.field === 'name' || col.field === 'linkText' || col.field === 'linkDestination') {
          field.required = true;
          field.errorMessage = `${field.label} is required`;
        }

        // Add cellEditor specific configurations
        if (col.cellEditor) {
          // Handle cellEditorParams if they exist
          if (col.cellEditorParams) {
            field.params = col.cellEditorParams;
          }

          // Handle popup editors
          if (col.cellEditorPopup) {
            field.popup = true;
          }

          // Handle specific editor types
          if (typeof col.cellEditor === 'string') {
            switch (col.cellEditor) {
              case 'agTextCellEditor':
              case 'agLargeTextCellEditor':
                field.type = 'text';
                if (col.cellEditor === 'agLargeTextCellEditor') {
                  field.multiline = true;
                }
                break;
              case 'agNumberCellEditor':
                field.type = 'number';
                if (col.cellEditorParams?.min) field.min = col.cellEditorParams.min;
                if (col.cellEditorParams?.max) field.max = col.cellEditorParams.max;
                break;
              case 'agDateCellEditor':
                field.type = 'date';
                break;
              case 'agSelectCellEditor':
                field.type = 'dropdown';
                field.options = col.cellEditorParams?.values || [];
                break;
            }
          } else if (col.cellEditor === BooleanEditorComponent) {
            field.type = 'checkbox';
            field.defaultValue = false;
          } else if (col.cellEditor === CalendarEditorComponent) {
            field.type = 'date';
            field.dateFormat = col.cellEditorParams?.format || 'mm/dd/yy';
          }
        }

        // Handle specific field types
        switch (col.field) {
          case 'enabled':
            field.type = 'checkbox';
            field.defaultValue = true;
            break;
          case 'linkText':
            field.type = 'text';
            field.placeholder = 'Enter link text';
            break;
          case 'linkDestination':
            field.type = 'text';
            field.placeholder = 'Enter link destination URL';
            break;
          case 'entityType':
            // For ImagerySettings, convert entityType to a dropdown with loaded entity types
            if (this.entity === 'ImagerySettings' && this.entityTypeOptions.length > 0) {
              field.type = 'dropdown';
              field.options = this.entityTypeOptions;
              field.optionLabel = 'label';
              field.optionValue = 'label';
              field.placeholder = 'Select entity type';
            }
            break;
          case 'showOnImageTypeList':
            // For ImagerySettings, set default value
            if (this.entity === 'ImagerySettings') {
              field.type = 'checkbox';
              field.defaultValue = true;
            }
            break;
          case 'showOnAssetBundleOnly':
            // For ImagerySettings, set default value
            if (this.entity === 'ImagerySettings') {
              field.type = 'checkbox';
              field.defaultValue = false;
            }
            break;
          case 'path':
            // For ImagerySettings, set default value to 'images/'
            if (this.entity === 'ImagerySettings') {
              field.defaultValue = 'images/';
            }
            break;
        }

        return field;
      });

    this.showDynamicForm = true;
  }

  handleDynamicFormSubmit(formData: any): void {
    // Refresh the grid data
    this.reloadDataSource(null);
  }

  handleDynamicFormCancel(): void {
    this.showDynamicForm = false;
  }

  private getFieldType(col: any): string {
    // First check if there's a cellEditor specified
    if (col.cellEditor) {
      if (typeof col.cellEditor === 'string') {
        switch (col.cellEditor) {
          case 'agTextCellEditor':
          case 'agLargeTextCellEditor':
            return 'text';
          case 'agNumberCellEditor':
            return 'number';
          case 'agDateCellEditor':
            return 'date';
          case 'agSelectCellEditor':
            return 'dropdown';
        }
      } else if (col.cellEditor === BooleanEditorComponent) {
        return 'checkbox';
      } else if (col.cellEditor === CalendarEditorComponent) {
        return 'date';
      }
    }
    
    // Fall back to type-based mapping if no cellEditor specified
    switch (col.type) {
      case 'number':
        return 'number';
      case 'boolean':
        return 'checkbox';
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  }

  private getDropdownOptions(field: string): any[] {
    // Return options based on the field type
    switch (field) {
      case 'type':
        return [
          { label: 'Type 1', value: 'type1' },
          { label: 'Type 2', value: 'type2' }
        ];
      case 'status':
        return [
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' }
        ];
      default:
        return [];
    }
  }

  async passSelectedItemsToAiAssistant() {
    console.log('passSelectedItemsToAiAssistant');
    console.log('Selected rows count:', this.selectedRowsSet.size);
    
    if (this.selectedRowsSet.size === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Selection',
        detail: 'Please select items to send to AI Assistant.',
      });
      return;
    }

    // Fetch and emit the selected data first
    let extraProps = {};
    if (this.tableInputParams?.populate) {
      extraProps = { populate: this.tableInputParams.populate };
    }
    
    const data = await this.fetchAllRowsFromBackend(
      this.tableInputParams?.select || 'id name thumbnail_ref', 
      this.selectedRowsSet, 
      extraProps
    );
    
    console.log('Selected data for AI Assistant:', data);
    this.selectedRows = data;
    
    // Emit the selection change event first
    this.selectionChange.emit(data);
    
    // Then emit the AI assistant request event - let parent component handle AI assistant logic
    this.aiAssistantRequested.emit();
  }
    
  // Popup form methods
  openPopupForm(): void {
    this.openPopupFormEvent.emit({action: 'new'});
  }

  openEditPopupForm(recordId: number): void {
    this.openPopupFormEvent.emit({action: 'edit', recordId: recordId});
  }
}
